import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import SettingsLandingCard from "./SettingsLandingCard";
import type {SettingsSection} from "./settingsNavigation";
import {SettingsSearchInput, SettingsSearchResults} from "./search/SettingsSearchPrimitives";
import type {SettingsSearchProps, SettingsSearchSection} from "./search/settingsSearch.types";
import {useSettingsSearch} from "./search/useSettingsSearch";

type Props = SettingsSearchProps & {
    sections: SettingsSection[]
}

const SettingsMobileLanding = ({sections, ...searchProps}: Props) => {
    const {t} = useTranslation()
    const search = useSettingsSearch(searchProps)
    const resultSections = React.useMemo<SettingsSearchSection[]>(() => [
        {title: 'Go to', results: search.goToResults},
        {title: 'Find', results: search.findResults},
        {title: 'Results', results: search.searchResults},
        {title: 'Create', results: search.createResults},
    ], [search.createResults, search.findResults, search.goToResults, search.searchResults])

    return (
        <Stack gap={2} width="100%" pt={2} pb={3}>
            <SettingsSearchInput search={search} mobile/>

            {search.isIdle ? (
                <Stack gap={1} width="100%">
                    {sections.map((section, index) => (
                        <React.Fragment key={section.id}>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                mt={index === 0 ? 0 : 2}
                                sx={{pl: 1.5, fontWeight: 500}}
                            >
                                {t(section.label)}
                            </Typography>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 1fr)',
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    '& > *:not(:last-child)': {
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.100',
                                    },
                                }}
                            >
                                {section.items.map(item => (
                                    <SettingsLandingCard
                                        key={item.id}
                                        to={item.path}
                                        label={t(item.label)}
                                    />
                                ))}
                            </Box>
                        </React.Fragment>
                    ))}
                </Stack>
            ) : (
                <SettingsSearchResults
                    search={search}
                    sections={resultSections}
                    scrollMode="page"
                />
            )}
        </Stack>
    )
}

export default SettingsMobileLanding
